import React, { CSSProperties, Fragment, useId } from "react";
import {
  Fieldset,
  Legend,
  Segment,
  SegmentInput,
  Thumb,
  Track,
} from "./SegmentedControl.styles";

export interface SegmentedControlOption<V extends string> {
  value: V;
  label: string;
  // Language of the label when it differs from the page (e.g. an endonym).
  lang?: string;
}

export interface SegmentedControlProps<V extends string> {
  name: string;
  value: V;
  options: ReadonlyArray<SegmentedControlOption<V>>;
  onChange: (value: V) => void;
  // Visible legend; pass ariaLabel instead when the context already names it.
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

// A small closed set of mutually exclusive options shown side by side. Built
// on native radios in a fieldset, so it is one tab stop, the arrow keys move
// and select, and forms and screen readers see an ordinary radio group.
const SegmentedControl = <V extends string>({
  name,
  value,
  options,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  className,
}: SegmentedControlProps<V>) => {
  const baseId = useId();
  const legendId = `${baseId}-legend`;
  // -1 when the value matches no option (a preference the profile does not
  // hold yet): no radio is checked, so nothing must look chosen either.
  const selectedIndex = options.findIndex((option) => option.value === value);
  const trackStyle = {
    "--count": options.length,
    "--index": Math.max(0, selectedIndex),
  } as CSSProperties;

  return (
    <Fieldset
      role="radiogroup"
      aria-labelledby={label ? legendId : undefined}
      aria-label={label ? undefined : ariaLabel}
      disabled={disabled}
      className={className}
    >
      {label && <Legend id={legendId}>{label}</Legend>}
      <Track style={trackStyle}>
        <Thumb aria-hidden="true" hidden={selectedIndex < 0} />
        {options.map((option) => {
          const id = `${baseId}-${option.value}`;
          const selected = option.value === value;
          return (
            <Fragment key={option.value}>
              <SegmentInput
                type="radio"
                id={id}
                name={name}
                value={option.value}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(option.value)}
              />
              <Segment htmlFor={id} $selected={selected} lang={option.lang}>
                {option.label}
              </Segment>
            </Fragment>
          );
        })}
      </Track>
    </Fieldset>
  );
};

export default SegmentedControl;
